import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ShieldAlert, Film } from "lucide-react";
import VideoShotCard from "./VideoShotCard";
import VideoVisualContractPanel from "./VideoVisualContractPanel"; // Sprint FGI-lite

/**
 * VideoStudioPanel — V1-A.
 *
 * Renders one VideoShotCard per shot exposed by the `prompts` stage.
 * Reads project.video_studio_enabled and the /video-studio/config
 * endpoint to surface the global kill switch, allowed providers, and
 * the daily cap counter. V1-A is manual-import only.
 */
export default function VideoStudioPanel({ project, reload }) {
    const [config, setConfig] = useState(null);
    const [providers, setProviders] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadConfig = useCallback(async () => {
        try {
            const { data } = await api.get(`/projects/${project.id}/video-studio/config`);
            setConfig(data);
        } catch (e) {
            console.warn("video-studio config load failed", e);
        }
    }, [project.id]);

    const loadProviders = useCallback(async () => {
        try {
            const { data } = await api.get(`/video-studio/providers`);
            setProviders(data);
        } catch (e) {
            // Endpoint may be missing in older preview pods — degrade gracefully.
            setProviders({ enabled: ["manual_import"], capabilities: {} });
        }
    }, []);

    const loadJobs = useCallback(async () => {
        try {
            const { data } = await api.get(`/projects/${project.id}/video-jobs?limit=200`);
            setJobs(data?.items || []);
        } catch (e) { console.warn("video-jobs load failed", e); }
        finally { setLoading(false); }
    }, [project.id]);

    useEffect(() => {
        loadConfig();
        loadJobs();
        loadProviders();
    }, [loadConfig, loadJobs, loadProviders]);

    // 3-sec polling for active jobs so users see status flips live.
    useEffect(() => {
        const hasActive = jobs.some(j =>
            ["queued", "submitted", "processing"].includes(j.status));
        if (!hasActive) return undefined;
        const t = setInterval(loadJobs, 3000);
        return () => clearInterval(t);
    }, [jobs, loadJobs]);

    const enable = async () => {
        try {
            await api.put(`/projects/${project.id}/video-studio/enabled`, { enabled: true });
            toast.success("Video Studio enabled for this project.");
            reload?.();
            await loadConfig();
        } catch (e) {
            toast.error(e?.userMessage || "Could not enable Video Studio");
        }
    };

    const shotsResolved = useMemo(() => extractShots(project), [project]);
    const jobsByShot = useMemo(() => {
        const map = {};
        jobs.forEach((j) => {
            (map[j.shot_id] ||= []).push(j);
        });
        return map;
    }, [jobs]);

    // iter60.2 — Union resolved shots with shot_ids that already have
    // jobs but were NOT returned by extractShots. This guards against
    // any future scenario where a job was created (e.g. via direct
    // backend submit during ops) for a shot the frontend resolver can't
    // see. We synthesise a minimal stub so the card still renders.
    const shots = useMemo(() => {
        const seen = new Set(shotsResolved.shots.map(s => s.shot_id));
        const synth = [];
        Object.keys(jobsByShot).forEach((sid) => {
            if (!sid || seen.has(sid)) return;
            synth.push({
                shot_id: sid,
                video_prompt: "",
                image_prompt: "",
                negative_prompt: "",
                _source: "jobs_only",
            });
        });
        return [...shotsResolved.shots, ...synth];
    }, [shotsResolved, jobsByShot]);

    const fallbackSource = shotsResolved.source;

    const globalDisabled = config && !config.global_enabled;
    const projectDisabled = !project.video_studio_enabled;

    if (globalDisabled) {
        return (
            <div className="card-base p-6 ceo-glow" data-testid="video-studio-disabled-global">
                <ShieldAlert className="w-5 h-5 text-rose-300 mb-3" />
                <h3 className="font-display text-lg mb-1">Video Studio is temporarily disabled.</h3>
                <p className="text-sm text-zinc-500">
                    An admin has paused video jobs across Furmais. Please try again later or contact support.
                </p>
            </div>
        );
    }

    if (projectDisabled) {
        return (
            <div className="card-base p-6 ceo-glow" data-testid="video-studio-disabled-project">
                <Film className="w-5 h-5 text-orange-300 mb-3" />
                <h3 className="font-display text-lg mb-1">Enable Video Studio for this project</h3>
                <p className="text-sm text-zinc-400 mb-4 max-w-xl">
                    Video Studio is optional in V1-A. Once enabled, you can upload videos generated externally
                    (e.g. Google Flow) for each storyboard shot, then approve them straight into QC.
                    Direct on-platform generation (Sora 2) arrives in V1-B.
                </p>
                <button
                    onClick={enable}
                    data-testid="video-studio-enable"
                    className="btn-primary text-sm"
                >
                    <Sparkles className="w-4 h-4" />
                    Enable Video Studio
                </button>
            </div>
        );
    }

    if (loading) {
        return <div className="card-base p-6 text-sm text-zinc-500" data-testid="video-studio-loading">Loading video studio…</div>;
    }

    if (shots.length === 0) {
        return (
            <div className="card-base p-6 text-sm text-zinc-500" data-testid="video-studio-empty">
                The Prompt Library and Storyboard stages have no shots yet, and
                no existing video jobs are attached to this project. Run Prompt
                Library or approve the Storyboard before submitting video jobs.
            </div>
        );
    }

    const cap = config?.daily_cap || { used: 0, cap: 35, remaining: 35 };
    const costCap = config?.daily_cost_cap || providers?.daily_cost_cap_usd
        ? (config?.daily_cost_cap || {
            cap_usd: providers?.daily_cost_cap_usd || 25,
            used_usd: 0,
            remaining_usd: providers?.daily_cost_cap_usd || 25,
        })
        : null;
    const allowed = (providers?.enabled || config?.allowed_providers || ["manual_import"]).join(", ");
    const soraOn = (providers?.enabled || []).some(n => n === "sora-2" || n === "sora-2-pro");
    const ffmpegReady = providers?.ffmpeg_ready !== false;   // default true

    // iter66 — Sora limitations panel sourced from iter64's pack.sora_limitations.
    // Fall back to a static set so the FE never renders empty even when the pack
    // hasn't been generated yet.
    const persistedPack = project?._video_prompt_pack || {};
    const soraLimitations = (
        (persistedPack.pack || persistedPack).sora_limitations?.items
        || project?.stages?.prompts?.output?.sora_limitations?.items
        || (soraOn ? [
            "Sora 2 currently outputs ONLY 1280x720 (16:9 landscape HD) via the integration SDK. 9:16 portrait requires a crop/post workflow.",
            "Sora dialogue / lip-sync is unreliable. Treat dialogue and VO as POST-PRODUCED.",
            "Sora supports exactly 4s / 8s / 12s clip lengths.",
            "Sora is NOT reliable for exact logo / text / dialogue rendering — verify in QC and re-issue if it slips.",
            "One paid Sora job per shot in beta. Cancel the running job before submitting a different duration / size.",
        ] : [])
    );

    // iter66 — Shot uniqueness report (iter64). Sourced from the same
    // persisted pack so a single page load already has the report.
    const uniquenessReport = (persistedPack.pack || persistedPack).uniqueness_report
        || project?.stages?.prompts?.output?.uniqueness_report
        || null;

    // iter66 — Continuity Bible readiness tier (missing / partial / ready).
    const bible = project?._continuity_bible || {};
    const bibleFilled = [
        bible.character_identity_lock, bible.wardrobe_lock,
        bible.location_lock, bible.lighting_lock,
        bible.logo_lock, bible.brand_color_lock,
    ].filter(v => typeof v === "string" && v.trim()).length;
    const bibleStatus = (
        bibleFilled >= 4 ? { tier: "ready",
            message: `Continuity Bible filled (${bibleFilled}/6 locks). Locks ride into every shot's prompt.` }
        : bibleFilled > 0 ? { tier: "partial",
            message: `Continuity Bible has ${bibleFilled}/6 locks. Fill the rest for stronger shot continuity.` }
        : { tier: "missing",
            message: "Continuity Bible is empty. Add character / wardrobe / location / lighting locks to keep shots consistent." }
    );

    return (
        <div data-testid="video-studio-panel" className="space-y-5">
            <div className="card-base p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="label-micro mb-1">Video Studio · {soraOn ? "V1-B" : "V1-A"}</div>
                    <div className="text-sm text-zinc-300">
                        {soraOn
                            ? "Direct generation via Sora 2 + manual import. Per-shot only."
                            : "Manual import from Google Flow. Direct generation arrives in V1-B."}
                    </div>
                </div>
                <div className="flex items-center gap-4 text-[11px]">
                    <span data-testid="video-studio-cap"
                          className="text-zinc-500 uppercase tracking-wider">
                        Daily cap: {cap.used} / {cap.cap}
                    </span>
                    {soraOn && costCap && (
                        <span
                            data-testid="video-studio-cost-cap"
                            className={`uppercase tracking-wider ${
                                Number(costCap.remaining_usd) <= 0
                                    ? "text-rose-400"
                                    : Number(costCap.used_usd) > 0.6 * Number(costCap.cap_usd)
                                        ? "text-amber-300"
                                        : "text-zinc-500"
                            }`}
                            title={`Sora paid-cost cap is ${costCap.scope || "per_owner_24h_paid_only"}.`}
                        >
                            Sora cost: ${Number(costCap.used_usd || 0).toFixed(2)} / ${Number(costCap.cap_usd || 0).toFixed(2)}
                        </span>
                    )}
                    <span className="text-zinc-600 uppercase tracking-wider">
                        Providers: {allowed}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {soraOn && !ffmpegReady && (
                    <div
                        data-testid="video-studio-ffmpeg-banner"
                        className="lg:col-span-2 rounded-md border border-rose-700/60 bg-rose-950/30 px-3 py-2 text-[12px] text-rose-200"
                    >
                        <strong>FFmpeg not installed on the backend.</strong>{" "}
                        Sora outputs cannot be re-muxed for browser playback,
                        so paid Sora submissions are blocked (HTTP 503).
                        Install <code>ffmpeg</code> on the deployment image and
                        restart the backend before enabling Sora.
                    </div>
                )}
                {/* iter66 — Sora capability limitations panel. Sourced from
                    pack.sora_limitations (iter64). Visible BEFORE any paid
                    generation so the operator knows the tradeoffs. */}
                {soraOn && soraLimitations.length > 0 && (
                    <details
                        data-testid="video-studio-sora-limitations"
                        className="lg:col-span-2 rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2 text-[12px] text-zinc-300 group"
                    >
                        <summary className="cursor-pointer select-none flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-300">
                            <ShieldAlert className="w-3.5 h-3.5" />
                            Sora 2 limitations — read before generation
                            <span className="ml-auto text-zinc-600 group-open:hidden">show</span>
                            <span className="ml-auto text-zinc-600 hidden group-open:inline">hide</span>
                        </summary>
                        <ul className="mt-2 space-y-1 text-[11.5px] text-zinc-300 leading-relaxed list-disc list-inside">
                            {soraLimitations.map((line, i) => (
                                <li key={i}>{line}</li>
                            ))}
                        </ul>
                    </details>
                )}
                {/* iter66 — Shot uniqueness warning. Warning-tier surfaces
                    here without blocking; blocking-tier is enforced at
                    submit time on the backend. */}
                {uniquenessReport && (uniquenessReport.warning_count + uniquenessReport.block_count) > 0 && (
                    <div
                        data-testid="video-studio-uniqueness-banner"
                        className={`lg:col-span-2 rounded-md border px-3 py-2 text-[12px] flex items-start gap-2 ${
                            uniquenessReport.blocking
                                ? "border-rose-700/60 bg-rose-950/30 text-rose-200"
                                : "border-amber-700/40 bg-amber-950/20 text-amber-200/90"
                        }`}
                    >
                        <span className="inline-flex items-center gap-1 rounded-sm border border-current/40 bg-current/10 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider">
                            uniqueness · {uniquenessReport.blocking ? "blocking" : "warning"}
                        </span>
                        <span className="leading-relaxed">
                            {uniquenessReport.summary}
                            {uniquenessReport.blocking && (
                                <> — submissions for flagged shots will return HTTP 409 until you differentiate them or set <code>allow_duplicate=true</code>.</>
                            )}
                        </span>
                    </div>
                )}
                {/* iter66 — Continuity Bible readiness pill. */}
                {soraOn && (
                    <div
                        data-testid="video-studio-bible-status"
                        className={`lg:col-span-2 rounded-md border px-3 py-2 text-[11.5px] flex items-center gap-2 ${
                            bibleStatus.tier === "ready" ? "border-emerald-700/40 bg-emerald-950/20 text-emerald-200/90"
                                : bibleStatus.tier === "partial" ? "border-amber-700/40 bg-amber-950/20 text-amber-200/90"
                                : "border-zinc-800 bg-zinc-950/60 text-zinc-400"
                        }`}
                    >
                        <span className="inline-flex items-center gap-1 rounded-sm border border-current/40 bg-current/10 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider">
                            continuity · {bibleStatus.tier}
                        </span>
                        <span>{bibleStatus.message}</span>
                    </div>
                )}
                {fallbackSource === "sora_pack" && (
                    <div
                        data-testid="video-studio-sorapack-banner"
                        className="lg:col-span-2 rounded-md border border-emerald-700/40 bg-emerald-950/20 px-3 py-2 text-[12px] text-emerald-200/90 flex items-start gap-2"
                    >
                        <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-600/40 bg-emerald-900/30 px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-emerald-200">
                            source: sora prompt pack
                        </span>
                        <span>
                            Using the <strong>Sora Prompt Pack</strong> (canonical saved
                            pack) — each shot&apos;s duration and aspect were preset from
                            prompt-pack metadata. Override per-shot below if needed.
                        </span>
                    </div>
                )}
                {fallbackSource === "storyboard" && (
                    <div
                        data-testid="video-studio-fallback-banner"
                        className="lg:col-span-2 rounded-md border border-amber-700/40 bg-amber-950/20 px-3 py-2 text-[12px] text-amber-200/90"
                    >
                        Using approved <strong>Storyboard</strong> fallback —
                        the Prompt Library output is missing or was cancelled.
                        Shots below come from <code>storyboard.scenes[*].shots</code>;
                        re-run Prompt Library to upgrade these to operator-refined prompts.
                    </div>
                )}
                {/* Sprint FGI-lite — Video Visual Contract Panel. */}
                <div className="lg:col-span-2">
                    <VideoVisualContractPanel projectId={project.id} provider="sora-2" />
                </div>
                {shots.map((shot) => (
                    <VideoShotCard
                        key={shot.shot_id}
                        project={project}
                        shot={shot}
                        jobs={jobsByShot[shot.shot_id] || []}
                        onChange={loadJobs}
                        providers={providers}
                    />
                ))}
            </div>
        </div>
    );
}

function extractShots(project) {
    // iter61 — Mirror the backend `resolve_prompt_source_for_shot`:
    //   1) Canonical saved Sora-2 prompt pack from db.video_prompt_packs
    //      (hydrated by GET /api/projects/{id} into project._video_prompt_pack)
    //   2) Sora-2 prompt pack embedded in project.stages.video_prompt_pack
    //      or project.stages.prompts.output (legacy embedded copy)
    //   3) Prompt Library row         (stages.prompts.output.prompts[*])
    //   4) Storyboard fallback        (stages.storyboard.output.storyboard.scenes[*].shots[*])
    //
    // Returns:
    //   { shots: [...], source: "sora_pack" | "prompts" | "storyboard" | "empty" }
    //
    // Each shot carries `_source` so the card can label its origin.
    const pickSoraPack = (row) => {
        const pp = row?.provider_prompts || {};
        return pp["sora-2"] || pp["sora_2"] || pp["sora"] || null;
    };

    // iter62 — Step 1: canonical saved pack (db.video_prompt_packs).
    // The pack stores provider variants as a map keyed by provider name:
    //   { provider_prompts: { "sora-2": [ { shot_id, ideal_prompt, ... }, ... ] } }
    const canonical = project?._video_prompt_pack;
    if (canonical && typeof canonical === "object") {
        const provMap = (canonical.pack || canonical).provider_prompts || {};
        const arr =
            provMap["sora-2"] ||
            provMap["sora_2"] ||
            provMap["sora"] ||
            [];
        const fromCanonical = [];
        (arr || []).forEach((entry) => {
            if (!entry || !entry.shot_id) return;
            if (!entry.ideal_prompt) return;
            fromCanonical.push({
                shot_id: entry.shot_id,
                video_prompt: String(entry.ideal_prompt).slice(0, 6000),
                image_prompt: entry.image_prompt || "",
                negative_prompt: entry.negative_prompt || "",
                sora: entry.sora || null,  // {duration_seconds, size, ...}
                _source: "sora_pack",
                _source_origin: "canonical_pack_store",
            });
        });
        if (fromCanonical.length > 0) {
            return { shots: fromCanonical, source: "sora_pack" };
        }
    }

    // 2) Try the Sora prompt pack on either stage (video_prompt_pack OR prompts).
    const candidates = [];
    const pp = ((project?.stages?.video_prompt_pack?.output) || {});
    candidates.push(...(pp.shots || pp.items || []));
    const po = (project?.stages?.prompts?.output || {});
    candidates.push(...(po.prompts || po.items || []));
    const fromSora = [];
    candidates.forEach((row) => {
        if (!row || !row.shot_id) return;
        const block = pickSoraPack(row);
        if (block && block.ideal_prompt) {
            fromSora.push({
                shot_id: row.shot_id,
                video_prompt: String(block.ideal_prompt).slice(0, 6000),
                image_prompt: row.image_prompt || "",
                negative_prompt: block.negative_prompt || row.negative_prompt || "",
                sora: block.sora || null,        // {duration_seconds, size, ...}
                _source: "sora_pack",
                _source_origin: "embedded_stage",
            });
        }
    });
    if (fromSora.length > 0) {
        return { shots: fromSora, source: "sora_pack" };
    }

    // 3) Prompt Library rows (operator-refined).
    const fromPrompts = (po.prompts || po.items || [])
        .filter((r) => r && r.shot_id && (r.video_prompt || "").trim())
        .map((r) => ({
            shot_id: r.shot_id,
            video_prompt: r.video_prompt || "",
            image_prompt: r.image_prompt || "",
            negative_prompt: r.negative_prompt || "",
            sora: null,
            _source: "prompts",
        }));
    if (fromPrompts.length > 0) {
        return { shots: fromPrompts, source: "prompts" };
    }

    // 4) Storyboard fallback.
    const sbInner = (((project?.stages?.storyboard?.output) || {}).storyboard) || {};
    const fromStoryboard = [];
    (sbInner.scenes || []).forEach((sc) => {
        (sc?.shots || sc?.frames || []).forEach((sh) => {
            if (!sh || !sh.shot_id) return;
            const action = (sh.action || sh.description || "").trim();
            const imagePrompt = (sh.image_prompt || "").trim();
            const parts = [];
            if (imagePrompt) parts.push(imagePrompt);
            if (action) parts.push(`SHOT ACTION: ${action}`);
            const base = parts.join("\n\n").trim();
            fromStoryboard.push({
                shot_id: sh.shot_id,
                video_prompt: base,
                image_prompt: imagePrompt,
                negative_prompt: "",
                sora: sh.duration_seconds
                    ? { duration_seconds: sh.duration_seconds }
                    : null,
                _source: "storyboard",
            });
        });
    });
    if (fromStoryboard.length > 0) {
        return { shots: fromStoryboard, source: "storyboard" };
    }
    return { shots: [], source: "empty" };
}
